import React from 'react';
import { FaCrown } from 'react-icons/fa';
import { FRAME_CATALOG, FRAME_KEYFRAMES } from '../../utils/frameCatalog';

/**
 * Composant Avatar
 * Le cadre change de couleur et d'effet selon le niveau du joueur :
 * - Niv 1-4   : gris (Débutant)
 * - Niv 5-9   : vert (Intermédiaire)
 * - Niv 10-19 : bleu (Confirmé)
 * - Niv 20-29 : orange (Avancé)
 * - Niv 30-49 : or + glow (Expert)
 * - Niv 50+   : violet + glow animé (Légende)
 *
 * Un cadre cosmétique acheté en boutique (user.equipped_frame_id) remplace
 * le cadre lié au niveau quand il est présent.
 *
 * Un badge couronne doré se superpose en bas à droite pour les membres Premium
 * (dès que user.plan === 'premium' est transmis par l'appelant).
 */
function Avatar({ user, size = 'md', onClick, className = '', showBorder = false }) {
  const sizeMap = {
    xs:  { cls: 'w-8 h-8 text-xs',   ring: 40, crown: 14 },
    sm:  { cls: 'w-10 h-10 text-sm', ring: 48, crown: 16 },
    md:  { cls: 'w-12 h-12 text-base', ring: 56, crown: 18 },
    lg:  { cls: 'w-16 h-16 text-xl', ring: 72, crown: 22 },
    xl:  { cls: 'w-20 h-20 text-2xl', ring: 88, crown: 26 },
    '2xl': { cls: 'w-24 h-24 text-3xl', ring: 104, crown: 30 },
  };

  // Couleur de cadre selon le niveau
  const getBorderStyle = (level = 1) => {
    if (level >= 50) return {
      border: '3px solid #a855f7',
      boxShadow: '0 0 12px #a855f7, 0 0 24px rgba(168,85,247,0.4)',
      animation: 'legendPulse 2s ease-in-out infinite',
    };
    if (level >= 30) return {
      border: '3px solid #f0b429',
      boxShadow: '0 0 10px rgba(240,180,41,0.7), 0 0 20px rgba(240,180,41,0.3)',
    };
    if (level >= 20) return {
      border: '3px solid #f97316',
      boxShadow: '0 0 8px rgba(249,115,22,0.5)',
    };
    if (level >= 10) return {
      border: '3px solid #3b82f6',
      boxShadow: '0 0 8px rgba(59,130,246,0.5)',
    };
    if (level >= 5) return {
      border: '3px solid #22c55e',
      boxShadow: '0 0 6px rgba(34,197,94,0.4)',
    };
    return {
      border: '2px solid rgba(148,163,184,0.5)',
    };
  };

  // Couleurs de fond selon l'initiale
  const colors = [
    '#7c3aed', '#2563eb', '#4f46e5',
    '#db2777', '#ea580c', '#0d9488',
    '#0891b2', '#e11d48',
  ];
  const getColor = (name) => {
    if (!name) return colors[0];
    return colors[name.charCodeAt(0) % colors.length];
  };

  // Un joueur qui a déjà prestigé a forcément dépassé le plafond au moins une
  // fois — il garde visuellement le cadre Légende même si son niveau affiché
  // est remis à 1 (cf. LevelBadge, même logique).
  const rawLevel = user?.level || user?.niveau || 1;
  const level = (user?.prestige_level || 0) > 0 ? Math.max(rawLevel, 50) : rawLevel;
  const initiale = user?.pseudo ? user.pseudo[0].toUpperCase() : '?';
  const bgColor = getColor(user?.pseudo);
  const { cls, crown } = sizeMap[size] || sizeMap.md;
  const isPremiumUser = user?.plan === 'premium';

  // Un cadre acheté en boutique est visible partout (c'est tout l'intérêt du
  // cosmétique), contrairement au cadre lié au niveau qui reste réservé aux
  // contextes où showBorder est explicitement demandé (évite le bruit visuel
  // dans les listes denses comme le classement).
  const equippedFrame = user?.equipped_frame_id ? FRAME_CATALOG[user.equipped_frame_id] : null;
  const borderStyle = equippedFrame ? equippedFrame.style : (showBorder ? getBorderStyle(level) : {});
  const cursorStyle = onClick ? { cursor: 'pointer' } : {};

  const commonStyle = {
    borderRadius: '50%',
    flexShrink: 0,
    transition: 'box-shadow 0.3s, border-color 0.3s',
    ...borderStyle,
    ...cursorStyle,
  };

  const crownBadge = isPremiumUser && (
    <div
      title="Membre Premium"
      className="absolute flex items-center justify-center rounded-full"
      style={{
        bottom: -2,
        right: -2,
        width: crown,
        height: crown,
        background: 'linear-gradient(135deg, #f0b429, #c97d08)',
        border: '2px solid #0d2b1a',
        boxShadow: '0 0 6px rgba(240,180,41,0.8)',
      }}
    >
      <FaCrown size={crown * 0.55} color="#0d2b1a" />
    </div>
  );

  if (user?.avatar_url || user?.avatar) {
    return (
      <div className="relative inline-flex" style={{ lineHeight: 0 }}>
        <style>{`
          @keyframes legendPulse {
            0%, 100% { box-shadow: 0 0 12px #a855f7, 0 0 24px rgba(168,85,247,0.4); }
            50%       { box-shadow: 0 0 20px #a855f7, 0 0 40px rgba(168,85,247,0.7); }
          }
          ${FRAME_KEYFRAMES}
        `}</style>
        <img
          src={user.avatar_url || user.avatar}
          alt={user.pseudo || 'Avatar'}
          onClick={onClick}
          loading="lazy"
          className={`${cls} object-cover ${className}`}
          style={commonStyle}
        />
        {crownBadge}
      </div>
    );
  }

  return (
    <div className="relative inline-flex" style={{ lineHeight: 0 }}>
      <style>{`
        @keyframes legendPulse {
          0%, 100% { box-shadow: 0 0 12px #a855f7, 0 0 24px rgba(168,85,247,0.4); }
          50%       { box-shadow: 0 0 20px #a855f7, 0 0 40px rgba(168,85,247,0.7); }
        }
        ${FRAME_KEYFRAMES}
      `}</style>
      <div
        onClick={onClick}
        className={`${cls} flex items-center justify-center font-bold text-white ${className}`}
        style={{
          background: `radial-gradient(circle at 35% 35%, ${bgColor}dd, ${bgColor})`,
          fontFamily: "'Rajdhani', sans-serif",
          ...commonStyle,
        }}
      >
        {initiale}
      </div>
      {crownBadge}
    </div>
  );
}

export default Avatar;
